import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "./ServicePages.css";
import hero from "./assets/WebsiteManagement/website-management-services-hero.png";
import value from "./assets/WebsiteManagement/small-business-web-design-management-accountants.png";
import process from "./assets/WebsiteManagement/website-for-accounting-firm-updates.png";
import sixStep from "./assets/WebsiteManagement/website-management-six-step-checklist.png";
import phone from "./assets/WebsiteManagement/attoli-free-website-check-call.png";
import Footer from "./Footer";

const WebManagement = () => {
  return (
    <div className="page-wrapper">
      <Helmet>
        <meta
          name="description"
          content="Attoli's website management services keep your accounting firm's online presence up-to-date and effective. Book a free call to see what needs fixing today."
        />
        <title>Website Management Services For Accountants | Attoli</title>
        <link
          rel="canonical"
          href="https://attoli.com/services/website-management-services-for-accountants"
        />
      </Helmet>

      <div className="servicePage-container">
        <section className="servicePage-section hero-section">
          <div className="servicePage-content">
            <div className="servicePage-left">
              <h1 className="servicePage-title">
                Website Management Services For{" "}
                <span className="hero-accent">Accountants</span>
              </h1>
              <p className="servicePage-description">
                Stop worrying about a website that breaks or falls out of date.
                We handle your website management needs from updates to
                monitoring every single month.
              </p>

              <ul className="trust-badges">
                <li>No lock in contracts</li>
                <li>Built only for accounting and bookkeeping firms</li>
                <li>Free 20 minute call before you commit to anything</li>
              </ul>

              <div className="cta-block">
                <Link to="/contact" className="cta-button">
                  Book My Free Website Check
                </Link>
              </div>
            </div>

            <div className="servicePage-right">
              <img
                src={hero}
                alt="Laptop displaying a clean accounting firm website with a checklist icon representing updates and maintenance"
                className="servicePage-image"
              />
            </div>
          </div>
        </section>

        <section className="servicePage-section pain-section">
          <div className="section-inner">
            <h2 className="section-title">
              Why Website Management Matters More Than A One Time Build
            </h2>
            <p className="body-copy">
              Most accounting firms hire someone to build a website once and
              never touch it again. Plugins go outdated. Security patches get
              missed. Pages slow down and nobody notices until a client
              complains.
            </p>
            <p className="body-copy">
              A slow or broken website costs you more than embarrassment. Google
              ranks slow sites lower. Visitors leave a page that takes too long
              to load. Every one of those visitors could have been a new client.
            </p>
            <p className="body-copy">
              Without ongoing website management small problems turn into big
              ones. A broken contact form quietly stops leads for weeks before
              anyone catches it.
            </p>
            <p className="body-copy body-copy-emphasis">
              A website is not a one time project. It needs steady care to keep
              working for your firm.
            </p>
          </div>
        </section>

        <section className="servicePage-section value-section">
          <div className="section-inner">
            <h2 className="section-title">
              What Our Small Business Web Design And Management Services Include
            </h2>
            <p className="body-copy">
              Attoli handles the ongoing website work most accounting firms
              never get around to. Here is what that looks like in practice.
            </p>

            <div className="value-grid">
              <div className="value-card">
                <h3>Uptime And Security Monitoring</h3>
                <p>
                  We monitor your site around the clock and fix issues before
                  they turn into downtime. A secure site protects your firm and
                  your clients data.
                </p>
              </div>
              <div className="value-card">
                <h3>Website Speed Optimization</h3>
                <p>
                  We keep your site loading fast on every device. Faster pages
                  keep visitors on your site and rank better with Google.
                </p>
              </div>
              <div className="value-card">
                <h3>Content And Page Updates</h3>
                <p>
                  We update your services hours and team information whenever
                  something changes. Your site always reflects the current state
                  of your firm.
                </p>
              </div>
              <div className="value-card">
                <h3>Broken Link And Error Fixes</h3>
                <p>
                  We find and fix broken links missing pages and error messages
                  before they cost you a client.
                </p>
              </div>
              <div className="value-card">
                <h3>Software And Plugin Updates</h3>
                <p>
                  We keep your website software current and patched. Outdated
                  software is one of the biggest security risks a site can have.
                </p>
              </div>
              <div className="value-card">
                <h3>Monthly Reporting</h3>
                <p>
                  We send a simple monthly report showing what was updated fixed
                  and improved on your site.
                </p>
              </div>
            </div>
            <img
              src={value}
              alt="Shield icon over a website mockup representing security and uptime monitoring"
              className="inline-image"
            />
          </div>
        </section>

        <section className="servicePage-section keyword-section">
          <div className="section-inner">
            <h2 className="section-title">
              What A Website For Accounting Firm Should Actually Do
            </h2>
            <p className="body-copy">
              Your website should do more than sit online looking nice. It
              should load fast. It should work on every phone and tablet. It
              should make it easy for a visitor to find your services and
              contact you.
            </p>
            <img
              src={process}
              alt="Calendar with checkmarks on different days representing regular ongoing website maintenance"
              className="inline-image"
            />
            <p className="body-copy">
              We treat your website the same way you treat your books. Checked
              regularly. Reconciled when something looks off. Never left to sit
              untouched for months at a time.
            </p>
          </div>
        </section>

        <section className="servicePage-section process-section">
          <div className="section-inner">
            <h2 className="section-title">
              How We Manage Accounting Firm Websites Step By Step
            </h2>

            <img
              src={sixStep}
              alt="Six step checklist for website management covering uptime security speed content links and reporting"
              className="inline-image"
            />
          </div>
        </section>
        <section className="servicePage-section faq-section">
          <div className="section-inner">
            <h2 className="section-title">Before You Book Your Call</h2>

            <details className="faq-item">
              <summary>How often do you update my site?</summary>
              <p>
                We check your site weekly and make updates as needed. Nothing
                sits broken for long.
              </p>
            </details>

            <details className="faq-item">
              <summary>Do I have to sign a long contract?</summary>
              <p>
                No. We work month to month. You stay because the results are
                working not because a contract locks you in.
              </p>
            </details>

            <details className="faq-item">
              <summary>What if my website was built by someone else?</summary>
              <p>
                That is fine. We can manage a site built by any developer or
                platform. No rebuild required. We work with many different
                website builders from Squarespace to WordPress and more.
              </p>
            </details>

            <details className="faq-item">
              <summary>What happens if my site goes down?</summary>
              <p>
                We get notified immediately and start fixing it right away. Most
                issues are resolved the same day.
              </p>
            </details>
          </div>
        </section>

        <section className="servicePage-section closer-section">
          <div className="section-inner closer-inner">
            <img
              src={phone}
              alt="Calendar with a call icon and the text free 20 minute call"
              className="inline-image closer-image"
            />

            <h2 className="section-title">
              Ready To Stop Worrying About Your Website?
            </h2>
            <p className="body-copy">
              Book a free 20 minute call. We will check your current site for
              security speed and other issues and show you exactly what needs
              attention.
            </p>
            <Link to="/contact" className="cta-button cta-button-large">
              Book A Free Call
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default WebManagement;
